import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useRecoilState } from 'recoil';
import { RIStyles } from 'pages/reference-implementation/Style';
import Btn from 'components/button';
import { commonStyles as style } from 'assets/style';
import Form from 'pages/reference-implementation/provisioning/RI003AWS/components/Form';
import { OrderPayload, isPrivilegedADGroupValid } from 'pages/reference-implementation/provisioning/RI003AWS/store';
import AwsCloudOnboardOrderId from './components/AwsCloudOnboardOrderId';
import ApplicationService from './components/ApplicationService';
// import BusinessCategory from './components/BusinessCategory';

const AwsAcc = (props) => {
    const { resource, handleDrawerAction } = props;
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [privilegedADGroupValid, setPrivilegedADGroupValid] = useRecoilState(isPrivilegedADGroupValid);

    const linkStyle = {
        color: '#4A90E2',
        textDecoration: 'underline',
    };

    // Snapshot the privileged AD group selection so it can be restored if the drawer is cancelled without saving.
    const initialPrivilegedGroupRef = React.useRef({
        privilegedADGroup: order?.privilegedADGroup,
        privilegedADGroupOwner: order?.privilegedADGroupOwner,
        privilegedADGroupOwnerDetails: order?.privilegedADGroupOwnerDetails,
        privilegedADGroupList: order?.privilegedADGroupList,
        isValid: privilegedADGroupValid,
    });

    const [formData, setFormData] = useState({
        awsCloudOnboardingOrderId: resource?.configureData?.awsCloudOnboardingOrderId || '',
        businessApplication: resource?.configureData?.businessApplication || '',
        businessApplicationDetails: resource?.configureData?.businessApplicationDetails || null,
        applicationService: resource?.configureData?.applicationService || '',
        department: resource?.configureData?.department || '',
        businessCategory: resource?.configureData?.businessCategory || '',
        vpcName: resource?.configureData?.vpcName || order?.vpcName || '',
        vpcId: resource?.configureData?.vpcId || order?.vpcId || '',
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [privilegedGroupError, setPrivilegedGroupError] = useState('');

    useEffect(() => {
        // resource is loaded asynchronously during edit, so sync formData once configureData becomes available
        if (resource?.configureData) {
            setFormData((prev) => ({
                ...prev,
                awsCloudOnboardingOrderId: resource?.configureData?.awsCloudOnboardingOrderId || prev.awsCloudOnboardingOrderId,
                businessApplication: resource?.configureData?.businessApplication || prev.businessApplication,
                businessApplicationDetails: resource?.configureData?.businessApplicationDetails || prev.businessApplicationDetails,
                applicationService: resource?.configureData?.applicationService || prev.applicationService,
                department: resource?.configureData?.department || prev.department,
                businessCategory: resource?.configureData?.businessCategory || prev.businessCategory,
                vpcName: resource?.configureData?.vpcName || order?.vpcName || prev.vpcName,
                vpcId: resource?.configureData?.vpcId || order?.vpcId || prev.vpcId,
            }));
        }
        // eslint-disable-next-line
    }, [resource?.configureData]);

    const getPrivilegedGroupError = () => {
        if (!order?.privilegedADGroup) return 'Please select a Privileged Active Directory Group option.';
        if (order?.privilegedADGroup === 'Yes' && !privilegedADGroupValid) return 'Please select at least one Privileged Group before saving.';
        if (order?.privilegedADGroup === 'No' && !order?.privilegedADGroupOwner) return 'Please provide a Privileged AD Group Owner.';
        return '';
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.awsCloudOnboardingOrderId) newErrors.awsCloudOnboardingOrderId = { type: 'required', message: 'AWS Cloud Onboard Order ID is required' };
        if (!formData.applicationService) {
            newErrors.applicationService = { type: 'required', message: 'Application Service is required' };
        } else if (order?.appProfile && formData.applicationService !== order?.appProfile) {
            newErrors.applicationService = {
                type: 'mismatch',
                message: (
                    <>
                        As part of validation, we found that the provided application service does not match the associated AWS Cloud Onboarding order. Please
                        verify your Cloud Onboarding order and provide the correct application service to proceed. If you have any questions, please feel free
                        to reach out to{' '}
                        <a href="mailto:dl-globalcmpoperations@pepsico.com" style={linkStyle}>
                            DL
                        </a>
                        .
                    </>
                ),
            };
        }
        // if (!formData.businessCategory) newErrors.businessCategory = { type: 'required', message: 'Business Category is required' };
        setErrors(newErrors);
        const privError = getPrivilegedGroupError();
        return { isValid: Object.keys(newErrors).length === 0 && !privError, errors: newErrors };
    };

    const handleOnChange = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        setTouched((prev) => ({ ...prev, [key]: true }));
    };

    const handlePopulate = (prePopulated) => {
        setFormData((prev) => ({ ...prev, ...prePopulated }));
    };

    const handleClearOrderId = () => {
        setFormData((prev) => ({
            ...prev,
            awsCloudOnboardingOrderId: '',
            businessApplication: '',
            businessApplicationDetails: null,
            applicationService: '',
            department: '',
            // businessCategory: '',
        }));
    };

    const shouldShowError = (field) => touched[field] && !!errors[field];

    const shouldShowApplicationServiceError = () =>
        !!errors.applicationService && (touched.applicationService || errors.applicationService.type === 'mismatch');

    useEffect(() => {
        if (Object.keys(touched).length > 0) validateForm();
    }, [formData]);

    const handleSubmit = () => {
        setTouched({
            awsCloudOnboardingOrderId: true,
            applicationService: true,
            // businessCategory: true,
        });
        const privError = getPrivilegedGroupError();
        setPrivilegedGroupError(privError);
        const checkFormValid = validateForm();
        handleDrawerAction(
            'save',
            {
                ...formData,
                vpcName: order?.vpcName || formData.vpcName,
                vpcId: order?.vpcId || formData.vpcId,
            },
            checkFormValid
        );
    };

    useEffect(() => {
        if (privilegedGroupError) {
            setPrivilegedGroupError(getPrivilegedGroupError());
        }
    }, [order?.privilegedADGroup, order?.privilegedADGroupOwner, privilegedADGroupValid]);

    const handleCancel = () => {
        const initial = initialPrivilegedGroupRef.current;
        setOrder((prev) => ({
            ...prev,
            privilegedADGroup: initial.privilegedADGroup,
            privilegedADGroupOwner: initial.privilegedADGroupOwner,
            privilegedADGroupOwnerDetails: initial.privilegedADGroupOwnerDetails,
            privilegedADGroupList: initial.privilegedADGroupList,
        }));
        setPrivilegedADGroupValid(initial.isValid);
        handleDrawerAction('cancel');
    };

    return (
        <Box style={RIStyles.tags.wrapper} className="ri-layout">
            <Box sx={RIStyles.tags.contentWrapper}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <AwsCloudOnboardOrderId
                            value={formData.awsCloudOnboardingOrderId}
                            onChange={handleOnChange}
                            onPopulate={handlePopulate}
                            onClear={handleClearOrderId}
                            isRequired={shouldShowError('awsCloudOnboardingOrderId')}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <ApplicationService
                            value={formData.applicationService}
                            onChange={handleOnChange}
                            isRequired={shouldShowApplicationServiceError()}
                            errorMessage={shouldShowApplicationServiceError() ? errors.applicationService.message : ''}
                        />
                    </Grid>
                    {/* <Grid item xs={6}>
                        <BusinessCategory value={formData.businessCategory} onChange={handleOnChange} isRequired={shouldShowError('businessCategory')} />
                    </Grid> */}
                    <Grid item xs={12}>
                        <Form.PrivilegedADGroup />
                        {privilegedGroupError && <Typography sx={{ color: '#d32f2f', fontSize: '12px', mt: 0.5, ml: 1.5 }}>{privilegedGroupError}</Typography>}
                    </Grid>
                </Grid>
            </Box>
            <Box className="footer" sx={RIStyles.tags.actionWrapper} gap={2}>
                <Btn className="custom-btn" variant="contained" sx={style.modalSaveButton} onClick={handleSubmit} data-testid="save-btn">
                    Save
                </Btn>
                <Btn className="custom-btn" variant="outlined" sx={style.modalCancelButton} onClick={handleCancel} data-testid="cancel-btn">
                    Cancel
                </Btn>
            </Box>
        </Box>
    );
};

AwsAcc.propTypes = {
    resource: PropTypes.any.isRequired,
    handleDrawerAction: PropTypes.func.isRequired,
};

export default AwsAcc;
